import { capitalizeFirstLetter, formatDate } from "helpers/utils.helper"
import { DATE_FORMAT, DATE_TIME_FORMAT } from "helpers/dateFormat.helper"
import { TableQuotationsStatuses } from "./TableQuotationsStatuses"
import { ButtonOptions } from "components/ui/buttons/ButtonOptions/ButtonOptions"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { Spin } from "components/ui/spin/Spin"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { ArrowIcon } from "components/ui/icons/quotations/ArrowIcon"
import { TableQuotationsDropdownItems } from "./TableQuotationsDropdownItems"
import {
	useDownloadQuotationDetailsWord,
	useDownloadQuotationResultCsv,
	useDownloadQuotationResultPdf,
} from "hooks/api/quotation/quotations.hook"
import { useDrawing } from "hooks/api/drawings/drawings.hook"
import { useResultCsvBusyQuotationId } from "store/resultCsv.store"
import { useResultPdfBusyQuotationId } from "store/resultPdf.store"
import { useDetailsWordBusyQuotationId } from "store/detailsWord.store"
import type { TQuotationTableData } from "types/quotations.type"
import type { ColumnsType } from "antd/es/table"
import style from "./TableQuotations.module.scss"

const { linkSource, quotationNameCell, quotationName, quotationVersion, editButton, editSpinner } = style

type TReviewedCounter = {
	reviewed: number
	total: number
}

const renderReviewedCounter = (value: TReviewedCounter) => `${value.reviewed}/${value.total}`

type Props = {
	// Owner, Admin or Super Admin (REQ-392) — see canActAsOwner.
	canActAsOwner: boolean
	drawingId: number
	renameQuotation: (quotationId: number) => void
	duplicateQuotation: (quotationId: number) => void
	editQuotation: (quotationId: number) => void
	loadingQuotationId: number | null
}

export const TableQuotationsColumns = ({
	canActAsOwner,
	drawingId,
	renameQuotation,
	duplicateQuotation,
	editQuotation,
	loadingQuotationId,
}: Props) => {
	const { downloadResultCsv } = useDownloadQuotationResultCsv()
	const csvBusyQuotationId = useResultCsvBusyQuotationId()
	const { downloadResultPdf } = useDownloadQuotationResultPdf()
	const pdfBusyQuotationId = useResultPdfBusyQuotationId()
	const { downloadDetailsWord } = useDownloadQuotationDetailsWord()
	const wordBusyQuotationId = useDetailsWordBusyQuotationId()
	// Read for its name only: every download is named "<original PDF>__quotation_<name>.csv|pdf|docx".
	// Already in the cache — the drawer around this table fetches the same query.
	const { data: drawing } = useDrawing(drawingId || null)

	const { getDropdownItems } = TableQuotationsDropdownItems({
		renameQuotation,
		duplicateQuotation,
		downloadResultCsv,
		downloadResultPdf,
		downloadDetailsWord,
		drawingName: drawing?.name,
		csvBusyQuotationId,
		pdfBusyQuotationId,
		wordBusyQuotationId,
	})

	const columns: ColumnsType<TQuotationTableData> = [
		{
			title: "NAME",
			dataIndex: "name",
			key: "name",
			render: (name, record) => (
				<div className={quotationNameCell}>
					<span className={quotationName}>{name}</span>
					{record.version != null && <span className={quotationVersion}>Version {record.version}</span>}
				</div>
			),
		},
		{
			title: "CREATED AT",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (createdAt) => formatDate(createdAt, "yyyy-MM-dd'T'HH:mm:ssXXX", DATE_TIME_FORMAT),
		},
		{
			title: "SOURCE",
			dataIndex: "source",
			key: "source",
			render: (source) => (
				<span onClick={(e) => e.stopPropagation()}>
					<a href={source.url} target="_blank" className={linkSource}>
						{`${capitalizeFirstLetter(source.type)} ${formatDate(source.creation_datetime, "yyyy-MM-dd'T'HH:mm:ssXXX", DATE_FORMAT)}`}
						<ArrowIcon />
					</a>
				</span>
			),
		},
		{
			title: "STATUS",
			dataIndex: "status",
			key: "status",
			render: (status) => <TableQuotationsStatuses status={status} />,
		},
		{
			title: "SUBMITTED AT",
			dataIndex: "submittedAt",
			key: "submittedAt",
			render: (submittedAt) =>
				submittedAt ? formatDate(submittedAt, "yyyy-MM-dd'T'HH:mm:ssXXX", DATE_TIME_FORMAT) : "-",
		},
		{
			title: "REVIEWED ELEVATIONS",
			dataIndex: "reviewedErefs",
			key: "reviewedErefs",
			render: renderReviewedCounter,
		},
		{
			title: "REVIEWED OBJECTS",
			dataIndex: "reviewedObjects",
			key: "reviewedObjects",
			render: renderReviewedCounter,
		},
	]

	if (canActAsOwner) {
		columns.push({
			title: "",
			dataIndex: "edit",
			key: "edit",
			render: (_, record) =>
				record.status === "in_progress" ? (
					loadingQuotationId === record.key ? (
						<div className={editSpinner}>
							<Spin size="small" />
						</div>
					) : (
						<span onClick={(e) => e.stopPropagation()}>
							<ButtonSecondary
								className={editButton}
								type="button"
								submittable={loadingQuotationId === null}
								onClick={() => editQuotation(record.key)}
							>
								Edit
							</ButtonSecondary>
						</span>
					)
				) : null,
		})
	}

	if (canActAsOwner) {
		columns.push({
			title: "",
			dataIndex: "action",
			key: "action",
			render: (_, record) => {
				const items = getDropdownItems({
					quotationId: record.key,
					quotationName: record.name,
					isSubmitted: record.status === "submitted",
				})
				if (!items?.length) return null

				// The trigger deliberately stays put while a CSV generates: swapping it for a spinner would
				// take Rename and Duplicate away for the duration. Progress lives in the toast, and the
				// menu item itself is what carries it in-place.
				return (
					<span onClick={(e) => e.stopPropagation()}>
						<Dropdown items={items}>
							<span>
								<ButtonOptions />
							</span>
						</Dropdown>
					</span>
				)
			},
		})
	}

	return columns
}
